import { CompanyAvatar } from "@/components/molecules/company-avatar";
import { PhoneInput } from "@/components/molecules/phone";
import { Button } from "@/components/ui/button";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/features/utils/i18n";
import { Link } from "react-router-dom";

export type SignupDetails = {
  companyName: string;
  email: string;
  phone: string;
  password: string;
  /** Invited members only — becomes their name in the team they join. */
  fullName?: string;
};

/** The company an invited visitor is about to join. */
export type JoinedCompany = {
  client: { id: string; name: string; logo: string | null };
  role: string;
  inviterEmail: string | null;
};

/**
 * The fields a new account needs. Nothing here is verified: the email is proven
 * by the OTP step that follows, the phone is only contact information.
 *
 * Two shapes, decided by whether the visitor is joining a company that already
 * exists: the company name is **required** from someone opening their own, and
 * **absent** — not optional — from an invited member, who is shown the company
 * they are joining instead and asked only for a password (their name and phone
 * are theirs to skip).
 */
export const AccountForm = (props: {
  values: SignupDetails;
  onChange: (values: SignupDetails) => void;
  onSubmit: () => void;
  isLoading?: boolean;
  isValid?: boolean;
  /** Set once the email has been proven: changing it would void the token. */
  emailLocked?: boolean;
  /** Joining a company that exists: no company of their own to name. */
  joining?: boolean;
  /** Which one, when we know it — worth showing, it is why they are here. */
  joiningCompany?: JoinedCompany | null;
}) => {
  const { t } = useTranslation();
  const { values, onChange, onSubmit, isLoading, isValid, emailLocked } = props;
  const joining = !!props.joining;

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isValid && !isLoading) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()} autoComplete="on">
      <CardHeader>
        <CardDescription>
          {joining ? t("signup.invitedDescription") : t("signup.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          {joining && !!props.joiningCompany && (
            <div className="flex items-center gap-3 rounded-lg border p-3">
              <CompanyAvatar
                logo={props.joiningCompany.client.logo}
                name={props.joiningCompany.client.name}
                size="sm"
              />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">
                  {props.joiningCompany.client.name}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {props.joiningCompany.inviterEmail
                    ? t("invitation.invitedBy", {
                        email: props.joiningCompany.inviterEmail,
                      })
                    : t("signup.joiningCompany")}
                </div>
              </div>
            </div>
          )}
          {!joining && (
            <div className="grid gap-2">
              <Label>{t("signup.companyName")}</Label>
              <Input
                required
                name="organization"
                autoComplete="organization"
                autoFocus
                placeholder={t("signup.companyNamePlaceholder")}
                value={values.companyName}
                onChange={(e) =>
                  onChange({ ...values, companyName: e.target.value })
                }
                disabled={isLoading}
                onKeyDown={submitOnEnter}
              />
            </div>
          )}
          {joining && (
            <div className="grid gap-2">
              <Label>{t("signup.fullNameOptional")}</Label>
              <Input
                name="name"
                autoComplete="name"
                autoFocus
                placeholder={t("signup.fullNamePlaceholder")}
                value={values.fullName || ""}
                onChange={(e) =>
                  onChange({ ...values, fullName: e.target.value })
                }
                disabled={isLoading}
                onKeyDown={submitOnEnter}
              />
            </div>
          )}
          <div className="grid gap-2">
            <Label>{t("email.label")}</Label>
            <Input
              type="email"
              required
              name="email"
              autoComplete="email username"
              placeholder={t("email.placeholder")}
              value={values.email}
              onChange={(e) => onChange({ ...values, email: e.target.value })}
              disabled={isLoading || emailLocked}
              className={emailLocked ? "bg-muted" : undefined}
              onKeyDown={submitOnEnter}
            />
          </div>
          <div className="grid gap-2">
            <Label>
              {joining ? t("signup.phoneOptional") : t("signup.phone")}
            </Label>
            <PhoneInput
              name="tel"
              autoComplete="tel"
              placeholder={t("signup.phonePlaceholder")}
              value={values.phone}
              onChange={(value) =>
                onChange({ ...values, phone: (value as string) || "" })
              }
              disabled={isLoading}
              onKeyDown={submitOnEnter}
            />
          </div>
          <div className="grid gap-2">
            <Label>{t("signup.password")}</Label>
            <Input
              type="password"
              required
              name="new-password"
              autoComplete="new-password"
              placeholder={t("signup.passwordPlaceholder")}
              value={values.password}
              onChange={(e) =>
                onChange({ ...values, password: e.target.value })
              }
              disabled={isLoading}
              onKeyDown={submitOnEnter}
            />
            <p className="text-xs text-muted-foreground">
              {t("signup.passwordRequirements")}
            </p>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col gap-2">
        <Button
          type="submit"
          className="w-full"
          onClick={onSubmit}
          disabled={!isValid || isLoading}
        >
          {isLoading ? t("actions.loading") : t("signup.createAccount")}
        </Button>
        <Button asChild type="button" variant="link" className="p-0">
          <Link to="/">{t("signup.alreadyHaveAccount")}</Link>
        </Button>
      </CardFooter>
    </form>
  );
};
